import {
  Dialog,
  Box,
  DialogContent,
  Button,
  DialogTitle,
  InputLabel,
  TextField,
  TextareaAutosize,
  useMediaQuery,
  useTheme,
  MenuItem,
  Select,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useState } from 'react';
import {
  useGetLandDetails,
  useMutateGetInTouch,
} from '../../../../api/details.ts';
import {
  useFetchUserAttributes,
  useGetCurrentUser,
} from '../../../../hooks/auth.ts';
import {
  GetInTouchOptions,
  GetInTouchRequest,
} from '../../../../models/details.models.tsx';
import { AccountRequiredModal } from '../../../../components/account-required-modal/account-required-modal.tsx';
import { LandSearchType } from '../../../../services/api';

interface GetInTouchFormProps {
  id: string;
  searchType: LandSearchType | string;
  selectedOption: GetInTouchOptions;
  setShowSnackbar: (value: boolean) => void;
  setShowModal: (value: boolean) => void;
  setSelectedOption: (value: GetInTouchOptions) => void;
  showModal: boolean;
}

export const GetInTouchForm = (props: GetInTouchFormProps) => {
  const {
    id,
    searchType,
    selectedOption,
    setShowSnackbar,
    setShowModal,
    showModal,
    setSelectedOption,
  } = props;
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const labelStyle = {
    color: 'white',
    fontSize: '12px',
    marginBottom: '4px',
  };

  const [anythingElse, setAnythingElse] = useState('');

  const { data: user } = useGetCurrentUser();
  const { data: userAttributes } = useFetchUserAttributes();

  const { data: details } = useGetLandDetails({ id, searchType });
  const { mutate, isPending } = useMutateGetInTouch();

  const handleSubmit = () => {
    const address = `${details?.address.address}, ${details?.address.city}, ${details?.address.state} ${details?.address.zip}`;
    const req: GetInTouchRequest = {
      sub: userAttributes?.sub || '',
      getInTouchReason: selectedOption,
      freeFormText: anythingElse,
      apn: details?.apn,
      address: address,
    };
    mutate(req, {
      onSuccess: () => {
        setShowModal(false);
        setShowSnackbar(true);
      },
    });
  };

  return (
    <Box data-testid="get-in-touch-modal">
      {!user?.userId ? (
        <AccountRequiredModal
          open={showModal}
          onClose={() => setShowModal(false)}
        />
      ) : (
        <Dialog
          className="auth-dialog"
          fullScreen={fullScreen}
          open={showModal}
          onClose={() => setShowModal(false)}
          aria-labelledby="share-dialog-title"
        >
          <Box
            className="container"
            sx={{ minWidth: { xs: 'initial', sm: '600px' } }}
          >
            <Box className="header">
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Button
                  onClick={() => setShowModal(false)}
                  sx={{ color: 'white' }}
                  variant="transparent"
                >
                  <CloseIcon />
                </Button>
              </Box>

              <DialogTitle
                sx={{ padding: '12px', fontSize: '45px', textAlign: 'center' }}
                id="share-dialog-title"
              >
                Get In Touch
              </DialogTitle>
            </Box>{' '}
            <DialogContent className="content">
              <InputLabel sx={labelStyle}>Topic</InputLabel>
              <Select
                className="text-input select homeStylePreference"
                label="Topic"
                value={selectedOption}
              >
                {Object.values(GetInTouchOptions).map((option) => (
                  <MenuItem
                    key={option}
                    value={option}
                    onClick={() => setSelectedOption(option)}
                  >
                    {option}
                  </MenuItem>
                ))}
              </Select>
              <Box className="field-group">
                <Box className="field">
                  <InputLabel sx={labelStyle}>First Name</InputLabel>
                  <TextField
                    fullWidth
                    className="text-input"
                    disabled
                    value={userAttributes?.given_name}
                  />
                </Box>
                <Box className="field">
                  <InputLabel sx={labelStyle}>Last Name</InputLabel>
                  <TextField
                    fullWidth
                    className="text-input"
                    disabled
                    value={userAttributes?.family_name}
                  />
                </Box>
              </Box>
              <Box className="field-group">
                <Box className="field">
                  <InputLabel sx={labelStyle}>Cell Phone</InputLabel>
                  <TextField
                    fullWidth
                    className="text-input"
                    disabled
                    value={userAttributes?.phone_number}
                  />
                </Box>
                <Box className="field">
                  <InputLabel sx={labelStyle}>Contact Preference</InputLabel>
                  <TextField
                    fullWidth
                    className="text-input"
                    disabled
                    value="Cell Phone"
                  />
                </Box>
              </Box>

              <Box className="field">
                <InputLabel sx={labelStyle}>Anything Else?</InputLabel>
                <TextareaAutosize
                  onChange={(e) => setAnythingElse(e.target.value)}
                  className="text-box"
                  placeholder="Tell us more here!"
                />
              </Box>
              <Button
                disabled={isPending}
                onClick={() => handleSubmit()}
                sx={{
                  width: '100%',
                  marginTop: '12px',
                  background: 'white',
                  textTransform: 'capitalize',
                }}
                variant="outlined"
              >
                {isPending ? 'Sending Message...' : 'Send Message'}
              </Button>
            </DialogContent>
          </Box>
        </Dialog>
      )}
    </Box>
  );
};
